
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useRole } from './useRole';
import { useToast } from '@/components/ui/use-toast';

interface Payment {
  id: string;
  created_at: string;
  mentorship_title: string;
  mentor_name: string;
  amount_cents: number;
  status: string;
  type: 'mentorship' | 'digital_content';
  service_title: string; // Título genérico para o serviço
}

export function usePlayerPayments() {
  const { user } = useRole();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchPayments = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log("💳 [usePlayerPayments] Carregando histórico completo de pagamentos...");
        
        // Buscar pagamentos de mentorias e conteúdos digitais em paralelo para otimizar
        const [mentorshipPayments, digitalContentPayments] = await Promise.all([
          // 1. Pagamentos de mentorias (tabela payments) - Query simplificada
          supabase
            .from('payments')
            .select(`
              id,
              created_at,
              amount_cents,
              status,
              session_id
            `)
            .eq('player_profile_id', user.id)
            .not('session_id', 'is', null),
            
          // 2. Pagamentos de conteúdos digitais (tabela digital_content_purchases)
          supabase
            .from('digital_content_purchases')
            .select(`
              id,
              created_at,
              purchase_price_cents,
              payment_status,
              digital_content:digital_content_id(
                id,
                title,
                mentor:mentor_id(
                  profiles:profile_id(
                    id,
                    nickname
                  )
                )
              )
            `)
            .eq('buyer_id', user.id)
            .eq('payment_status', 'completed')
        ]);

        console.log("💳 [usePlayerPayments] Dados carregados:", {
          mentorias: mentorshipPayments.data?.length || 0,
          conteudos: digitalContentPayments.data?.length || 0,
          errorMentorias: mentorshipPayments.error?.message,
          errorConteudos: digitalContentPayments.error?.message,
          mentorshipData: mentorshipPayments.data,
          digitalContentData: digitalContentPayments.data
        });

        const allPayments: Payment[] = [];

        // Processar pagamentos de mentorias
        if (mentorshipPayments.data && mentorshipPayments.data.length > 0) {
          console.log("💳 [usePlayerPayments] Processando mentorias:", mentorshipPayments.data);
          
          // Buscar dados das sessões para cada pagamento
          const sessionIds = mentorshipPayments.data.map(p => p.session_id).filter(Boolean);
          
          let sessionsData = [];
          if (sessionIds.length > 0) {
            console.log("💳 [usePlayerPayments] Buscando dados das sessões:", sessionIds);
            
            // Tentar primeiro uma query simples para verificar se conseguimos acessar as sessões
            const { data: simpleSessions, error: simpleError } = await supabase
              .from('sessions')
              .select('id, game_id, offering_id, mentor_id')
              .in('id', sessionIds);
              
            console.log("💳 [usePlayerPayments] Query simples das sessões:", {
              simpleSessions,
              simpleError,
              count: simpleSessions?.length || 0
            });
            
            // Usar sempre a query simples e buscar relacionamentos separadamente para evitar problemas de RLS
            if (simpleSessions && simpleSessions.length > 0) {
                console.log("💳 [usePlayerPayments] Usando dados da query simples:", simpleSessions);
                
                // Buscar dados dos jogos separadamente
                const gameIds = [...new Set(simpleSessions.map(s => s.game_id).filter(Boolean))];
                let gamesMap = new Map();
                
                if (gameIds.length > 0) {
                  const { data: games } = await supabase
                    .from('games')
                    .select('id, name')
                    .in('id', gameIds);
                  
                  if (games) {
                    games.forEach(game => gamesMap.set(game.id, game));
                  }
                }
                
                // Buscar dados das ofertas separadamente
                const offeringIds = [...new Set(simpleSessions.map(s => s.offering_id).filter(Boolean))];
                let offeringsMap = new Map();
                
                if (offeringIds.length > 0) {
                  const { data: offerings } = await supabase
                    .from('mentorship_offerings')
                    .select('id, title, description')
                    .in('id', offeringIds);
                  
                  if (offerings) {
                    offerings.forEach(offering => offeringsMap.set(offering.id, offering));
                  }
                }
                
                // Buscar dados dos mentores separadamente
                const mentorIds = [...new Set(simpleSessions.map(s => s.mentor_id).filter(Boolean))];
                let mentorsMap = new Map();
                
                if (mentorIds.length > 0) {
                  console.log("💳 [usePlayerPayments] Buscando dados dos mentores:", mentorIds);
                  
                  const { data: mentors, error: mentorsError } = await supabase
                    .from('mentor_info')
                    .select(`
                      id,
                      profile_id,
                      profiles(id, nickname)
                    `)
                    .in('id', mentorIds);
                  
                  console.log("💳 [usePlayerPayments] Dados dos mentores recebidos:", {
                    mentors,
                    error: mentorsError,
                    count: mentors?.length || 0
                  });
                  
                  if (mentors) {
                    mentors.forEach(mentor => mentorsMap.set(mentor.id, mentor));
                  }
                }
                
                // Combinar os dados
                sessionsData = simpleSessions.map(s => ({
                  ...s,
                  games: gamesMap.get(s.game_id) || null,
                  mentorship_offerings: offeringsMap.get(s.offering_id) || null,
                  mentor_info: mentorsMap.get(s.mentor_id) || null
                }));
                
                console.log("💳 [usePlayerPayments] Dados combinados:", sessionsData);
              } else if (simpleError) {
                console.error("❌ [usePlayerPayments] Erro na query simples:", simpleError);
              }
          }
          
          const formattedMentorships = mentorshipPayments.data.map((payment) => {
            const sessionData = sessionsData.find(s => s.id === payment.session_id);
            const mentorName = sessionData?.mentor_info?.profiles?.nickname || "Mentor";
            const gameName = sessionData?.games?.name || 'Jogo não especificado';
            
            console.log("💳 [usePlayerPayments] Debug completo da sessão:", {
              paymentId: payment.id,
              sessionId: payment.session_id,
              sessionFound: !!sessionData,
              sessionData: sessionData,
              offeringTitle: sessionData?.mentorship_offerings?.title,
              gameName: sessionData?.games?.name,
              mentorName: sessionData?.mentor_info?.profiles?.nickname,
              mentorInfo: sessionData?.mentor_info,
              allSessionsData: sessionsData
            });
            
            // Prioridade para o título: 1) Título da oferta, 2) Nome do jogo
            let mentorshipTitle = `Mentoria de ${gameName}`; // Fallback
            
            if (sessionData?.mentorship_offerings?.title) {
              mentorshipTitle = sessionData.mentorship_offerings.title;
            }
            
            console.log("💳 [usePlayerPayments] Título final determinado:", {
              offeringTitle: sessionData?.mentorship_offerings?.title,
              gameName,
              finalTitle: mentorshipTitle
            });
            
            return {
              id: `mentorship_${payment.id}`,
              created_at: payment.created_at,
              mentorship_title: mentorshipTitle,
              mentor_name: mentorName,
              amount_cents: payment.amount_cents,
              status: payment.status,
              type: 'mentorship' as const,
              service_title: mentorshipTitle
            };
          });
          
          console.log("💳 [usePlayerPayments] Mentorias formatadas:", formattedMentorships.length);
          allPayments.push(...formattedMentorships);
        }

        // Processar pagamentos de conteúdos digitais
        if (digitalContentPayments.data) {
          const formattedDigitalContents = digitalContentPayments.data.map((purchase) => {
            const mentorName = purchase.digital_content?.mentor?.profiles?.nickname || "Mentor";
            const contentTitle = purchase.digital_content?.title || 'Conteúdo Digital';
            
            return {
              id: `digital_content_${purchase.id}`,
              created_at: purchase.created_at,
              mentorship_title: contentTitle,
              mentor_name: mentorName,
              amount_cents: purchase.purchase_price_cents,
              status: purchase.payment_status,
              type: 'digital_content' as const,
              service_title: contentTitle
            };
          });
          allPayments.push(...formattedDigitalContents);
        }

        // Ordenar todos os pagamentos por data (mais recente primeiro)
        allPayments.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

        console.log("💳 [usePlayerPayments] Pagamentos processados:", {
          total: allPayments.length,
          mentorias: allPayments.filter(p => p.type === 'mentorship').length,
          conteudos: allPayments.filter(p => p.type === 'digital_content').length
        });

        setPayments(allPayments);
        
      } catch (error: any) {
        console.error('❌ [usePlayerPayments] Erro:', error);
        toast({
          variant: "destructive",
          title: "Erro ao carregar pagamentos",
          description: "Não foi possível carregar seus pagamentos. Tente novamente mais tarde."
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, [user?.id, toast]);

  return { payments, loading };
}
